import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, GraduationCap, Compass, ChevronRight, MapPin, Clock, ArrowRight } from 'lucide-react';
import { usePageMeta } from '../../../hooks/useSEO';
import BreadcrumbSchema from '../../../components/seo/BreadcrumbSchema';
import { FinalCTA } from './DubaiCostOfLivingPage';

/* Forward-looking 2026 event calendar — refreshed quarterly. */
const EVENTS = [
  {
    id: 'dubai-alumni-power-panel-mar-2026',
    icon: Users,
    type: 'Alumni Power Panel',
    title: 'Dubai Alumni Power Panel — DIFC Fintech Edition',
    date: '2026-03-22', startTime: '17:00', durationMin: 90,
    description: 'Four ZAFCO alumni currently working at DIFC fintech firms (Mashreq, Tabby, Network International) share the unfiltered story of student visa → Green Visa → Golden Visa in 2.5 years. Q&A is live.',
    format: 'Online (Zoom + LinkedIn Live)',
    cta: 'Engage with real alumni before you commit.',
  },
  {
    id: 'dubai-uni-masterclass-heriot-watt-apr-2026',
    icon: GraduationCap,
    type: 'University Masterclass',
    title: 'University Masterclass — Heriot-Watt Dubai (Engineering & AI)',
    date: '2026-04-12', startTime: '18:00', durationMin: 60,
    description: 'Heriot-Watt\u2019s Head of Admissions walks through the 2026 AI MSc curriculum, internship pipeline (G42, ENOC, ADNOC), and Petroleum-Engineering Golden-Visa track. Live AED tuition + scholarship reveal.',
    format: 'Online + In-person (ZAFCO Delhi Office)',
    cta: 'Engage with real alumni before you commit.',
  },
  {
    id: 'dubai-pr-skill-shortage-clinic-may-2026',
    icon: Compass,
    type: 'PR & Skill Shortage Clinic',
    title: 'PR / Skill Shortage Clinic — 2026 UAE Talent Demand',
    date: '2026-05-10', startTime: '11:00', durationMin: 75,
    description: 'Live walk-through of the UAE MoHRE 2026 shortage occupations list — which Dubai degrees map directly to Golden Visa Specialist Talent eligibility. Bring your transcript for a 1:1 fit-check.',
    format: 'Online (Zoom)',
    cta: 'Engage with real alumni before you commit.',
  },
  {
    id: 'dubai-alumni-power-panel-jul-2026',
    icon: Users,
    type: 'Alumni Power Panel',
    title: 'Dubai Alumni Power Panel — Luxury Hospitality Edition',
    date: '2026-07-19', startTime: '17:30', durationMin: 90,
    description: 'Five ZAFCO hospitality alumni from Emaar, Jumeirah, and Atlantis discuss how a 1-year MSc converted into permanent UAE residency through the 5-year Green Visa.',
    format: 'Online + In-person (ZAFCO Mumbai Office)',
    cta: 'Engage with real alumni before you commit.',
  },
];

export default function DubaiEventsWebinarsPage() {
  usePageMeta(
    'Dubai Events & Webinars — Alumni Panels, University Masterclasses & PR Clinics (2026)',
    'Live ZAFCO events for Dubai-bound Indian students: Alumni Power Panels, University Masterclasses (Heriot-Watt, AUD, UOWD), and PR / Skill-Shortage Clinics. Register free and engage with real alumni before you commit.'
  );

  /* Event JSON-LD (one Event per scheduled session). */
  useEffect(() => {
    const TAG = 'dubai-events-schema';
    document.getElementById(TAG)?.remove();
    const ld = EVENTS.map((e) => {
      const startsAt = `${e.date}T${e.startTime}:00+04:00`;
      const endsAt   = new Date(new Date(startsAt).getTime() + e.durationMin * 60000).toISOString();
      const fmt = e.format.toLowerCase();
      const isHybrid = fmt.includes('person') && fmt.includes('online');
      const isInPerson = fmt.includes('person') && !fmt.includes('online');
      const attendanceMode = isHybrid
        ? 'https://schema.org/MixedEventAttendanceMode'
        : isInPerson
          ? 'https://schema.org/OfflineEventAttendanceMode'
          : 'https://schema.org/OnlineEventAttendanceMode';
      return {
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: e.title,
        startDate: startsAt,
        endDate: endsAt,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: attendanceMode,
        description: e.description,
        organizer: {
          '@type': 'Organization',
          '@id': 'https://zafco.in/#organization',
          name: 'Zafco Human Resource Management',
          url: 'https://zafco.in/study-abroad/',
        },
        location: fmt.includes('person')
          ? [
              { '@type': 'VirtualLocation', url: 'https://zafco.in/study-abroad/dubai/events-webinars/' },
              { '@type': 'Place', name: 'ZAFCO Delhi Office', address: { '@type': 'PostalAddress', streetAddress: 'Ground Floor 5, Building No. 27B, Khizrabad, New Friends Colony', addressLocality: 'New Delhi', postalCode: '110025', addressCountry: 'IN' } },
            ]
          : { '@type': 'VirtualLocation', url: 'https://zafco.in/study-abroad/dubai/events-webinars/' },
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR', availability: 'https://schema.org/InStock', url: 'https://zafco.in/study-abroad/book-counselling/?source=dubai-event-' + e.id, validFrom: e.date },
        image: 'https://zafco.in/zafco-logo.png',
        performer: { '@id': 'https://zafco.in/#organization' },
      };
    });
    const s = document.createElement('script');
    s.id = TAG; s.type = 'application/ld+json'; s.text = JSON.stringify(ld);
    document.head.appendChild(s);
    return () => document.getElementById(TAG)?.remove();
  }, []);

  return (
    <div className="zaf-edu-platform bg-white text-slate-900" data-testid="dubai-events-page">
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://zafco.in/' },
        { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
        { name: 'Study in Dubai', url: 'https://zafco.in/study-abroad/dubai/' },
        { name: 'Events & Webinars', url: 'https://zafco.in/study-abroad/dubai/events-webinars/' },
      ]} />

      <section className="bg-gradient-to-br from-[#0F1B2C] to-[#162538] text-white py-12 md:py-16">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <Link to="/study-abroad/dubai/" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-4 hover:text-amber-300">
            <ChevronRight className="w-3 h-3 rotate-180" /> Dubai Hub
          </Link>
          <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-2">Live Intelligence Platforms</p>
          <h1 className="text-3xl md:text-5xl font-bold font-syne leading-tight">Dubai Events &amp; Webinars — <span className="text-[#D4AF37]">Engage Before You Commit</span></h1>
          <p className="mt-5 text-slate-300 max-w-3xl leading-relaxed">Brochures lie. Alumni don&apos;t. Every ZAFCO Dubai event puts you in the room with people who have already cleared the path you&apos;re planning to walk — currently-employed graduates, university admissions deans, and UAE policy specialists.</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold font-syne mb-3">Three Event Formats</h2>
          <p className="text-slate-600 mb-7 max-w-3xl">Designed to compress your research timeline from months into 90 minutes per session.</p>

          <div className="grid md:grid-cols-3 gap-5 mb-12">
            {[
              { icon: Users,         title: 'Alumni Power Panels',     copy: 'Cohort of 4–5 ZAFCO graduates currently working in Dubai. Unfiltered Q&A on visa journey, salary reality, daily life, and the things consultants don\u2019t mention.' },
              { icon: GraduationCap, title: 'University Masterclasses', copy: 'Direct access to admissions deans from Heriot-Watt, AUD, UOWD, Birmingham Dubai, Middlesex. Live AED tuition + scholarship reveals.' },
              { icon: Compass,       title: 'PR / Skill Shortage Clinics', copy: 'Walk-through of UAE MoHRE shortage occupations + Golden Visa Specialist Talent criteria. Bring your transcript for a 1:1 eligibility check.' },
            ].map((f) => (
              <div key={f.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center mb-3"><f.icon className="w-5 h-5 text-[#D4AF37]" /></div>
                <h3 className="font-bold font-syne text-[#0F1B2C] mb-2">{f.title}</h3>
                <p className="text-sm text-slate-700 leading-relaxed">{f.copy}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl md:text-3xl font-bold font-syne mb-3 flex items-center gap-2"><Calendar className="w-7 h-7 text-[#D4AF37]" /> Upcoming Sessions</h2>
          <p className="text-slate-600 mb-6">Free to attend. WhatsApp registration confirms your seat and adds you to the reminder list.</p>

          <div className="space-y-4">
            {EVENTS.map((e) => (
              <article key={e.id} className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6 hover:border-[#D4AF37] transition-colors" data-testid={`event-${e.id}`}>
                <div className="flex flex-col md:flex-row md:items-start gap-5">
                  <div className="md:w-44 flex-shrink-0">
                    <div className="inline-flex items-center gap-1.5 bg-[#0F1B2C] text-white text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1">
                      <e.icon className="w-3 h-3" /> {e.type}
                    </div>
                    <p className="mt-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">When</p>
                    <p className="text-sm font-bold text-[#0F1B2C]">{new Date(e.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                    <p className="text-xs text-slate-600 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> {e.startTime} UAE · {e.durationMin} min</p>
                    <p className="text-xs text-slate-600 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.format}</p>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold font-syne text-lg text-[#0F1B2C] mb-2">{e.title}</h3>
                    <p className="text-sm text-slate-700 leading-relaxed mb-4">{e.description}</p>
                    <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold mb-2">{e.cta}</p>
                    <a
                      href={`https://wa.me/919990533327?text=Hello%20ZAFCO%20-%20I%20want%20to%20register%20for%20${encodeURIComponent(e.title)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-amber-400 text-[#0F1B2C] font-bold px-5 py-2.5 rounded-full text-sm transition-colors"
                      data-testid={`event-register-${e.id}`}
                    >
                      Register on WhatsApp <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA source="dubai-events-webinars" />
    </div>
  );
}
